import { logger } from './logger';

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number; // 基礎延遲時間（毫秒）
  maxDelay?: number;  // 最大延遲時間（毫秒）
  backoffMultiplier?: number; // 退避倍數
  retryCondition?: (error: any) => boolean; // 判斷是否需要重試
  onRetry?: (error: any, attempt: number) => void; // 重試回調
}

export interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: Error;
  attempts: number;
}

/**
 * 重試機制工具類
 * 實作指數退避算法，適用於 API 調用和其他可能失敗的操作
 */
export class RetryHelper {
  /**
   * 使用指數退避算法重試操作
   */
  static async retryWithBackoff<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<RetryResult<T>> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 30000,
      backoffMultiplier = 2,
      retryCondition = () => true,
      onRetry
    } = options;

    let lastError: Error = new Error('Unknown error');
    let attempts = 0;

    for (let i = 0; i <= maxRetries; i++) {
      attempts = i + 1;
      
      try {
        logger.service.debug(`Attempting operation (attempt ${attempts}/${maxRetries + 1})`);
        const result = await operation();
        
        if (i > 0) {
          logger.service.info(`Operation succeeded after ${attempts} attempts`);
        }
        
        return {
          success: true,
          data: result,
          attempts
        };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        logger.service.warn(`Operation failed on attempt ${attempts}`, { error: lastError.message });

        // 如果這是最後一次嘗試，直接失敗
        if (i === maxRetries) {
          break;
        }

        // 檢查是否應該重試
        if (!retryCondition(lastError)) {
          logger.service.info(`Retry condition failed, stopping retries`);
          break;
        }

        // 計算延遲時間（指數退避 + 隨機抖動 jitter)
        // INFRA-02:無 jitter 時,多分頁/多用戶同時失敗會同步重試(thundering herd)
        const baseBackoff = Math.min(
          baseDelay * Math.pow(backoffMultiplier, i),
          maxDelay
        );
        // 全幅抖動:延遲落在 [baseBackoff/2, baseBackoff] 區間
        const delay = Math.round(baseBackoff * (0.5 + Math.random() * 0.5));

        logger.service.debug(`Waiting ${delay}ms before retry...`);

        // 調用重試回調
        if (onRetry) {
          onRetry(lastError, attempts);
        }

        // 等待指定時間
        await this.sleep(delay);
      }
    }

    logger.service.error(`Operation failed after ${attempts} attempts`, lastError);
    
    return {
      success: false,
      error: lastError,
      attempts
    };
  }

  /**
   * 快速重試（無退避延遲）
   * 適用於快速失敗的操作
   */
  static async quickRetry<T>(
    operation: () => Promise<T>,
    maxRetries: number = 2
  ): Promise<T> {
    const result = await this.retryWithBackoff(operation, {
      maxRetries,
      baseDelay: 100,
      maxDelay: 500,
      backoffMultiplier: 1.5
    });

    if (result.success) {
      return result.data!;
    }
    
    throw result.error;
  }

  /**
   * 網路相關操作重試
   * 針對網路錯誤進行優化的重試策略
   */
  static async networkRetry<T>(
    operation: () => Promise<T>,
    options: Partial<RetryOptions> = {}
  ): Promise<T> {
    const result = await this.retryWithBackoff(operation, {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 10000,
      backoffMultiplier: 2,
      retryCondition: (error) => {
        // 只對網路相關錯誤進行重試
        const errorMessage = error?.message?.toLowerCase() || '';
        return (
          errorMessage.includes('network') ||
          errorMessage.includes('timeout') ||
          errorMessage.includes('connection') ||
          errorMessage.includes('fetch') ||
          errorMessage.includes('cors') ||
          error?.code === 'NETWORK_ERROR'
        );
      },
      ...options
    });

    if (result.success) {
      return result.data!;
    }
    
    throw result.error;
  }

  /**
   * API 調用重試
   * 專門用於 API 調用的重試機制
   */
  static async apiRetry<T>(
    operation: () => Promise<T>,
    options: Partial<RetryOptions> = {}
  ): Promise<T> {
    const result = await this.retryWithBackoff(operation, {
      maxRetries: 2, // 重試次數 3 → 2
      baseDelay: 3000, // 基本延遲 3 秒
      maxDelay: 20000, // 最大延遲 20 秒
      backoffMultiplier: 2.5, // 退避倍數
      retryCondition: (error) => {
        // API 相關錯誤的重試條件
        const status = error?.status || error?.response?.status;
        const errorMessage = error?.message?.toLowerCase() || '';

        // 不重試的狀態碼（客戶端錯誤）
        const nonRetryableStatuses = [400, 401, 403, 404, 422];
        if (status && nonRetryableStatuses.includes(status)) {
          return false;
        }

        // DATA-09:無 status 的「斷路器開啟」錯誤屬本地熔斷,重試只會加速失敗 → 不重試
        if (!status && typeof errorMessage === 'string' && errorMessage.includes('circuit breaker is open')) {
          return false;
        }

        // 重試服務器錯誤和網路錯誤
        return (
          !status || // 沒有狀態碼（網路錯誤）
          status >= 500 || // 服務器錯誤
          errorMessage.includes('network') ||
          errorMessage.includes('timeout') ||
          errorMessage.includes('unavailable')
        );
      },
      onRetry: (error, attempt) => {
        // 只在最後一次重試時記錄警告，中間的重試使用debug級別
        const logLevel = attempt === 2 ? 'warn' : 'debug';
        if (logLevel === 'warn') {
          logger.api.warn(`API call final retry (attempt ${attempt})`, {
            error: error?.message,
            status: error?.status
          });
        } else {
          logger.api.debug(`API call retry (attempt ${attempt})`, { error: error?.message });
        }
      },
      ...options
    });

    if (result.success) {
      return result.data!;
    }
    
    throw result.error;
  }

  /**
   * 資料庫操作重試
   * 專門用於 Firestore 操作的重試機制
   */
  static async databaseRetry<T>(
    operation: () => Promise<T>,
    options: Partial<RetryOptions> = {}
  ): Promise<T> {
    const result = await this.retryWithBackoff(operation, {
      maxRetries: 2,
      baseDelay: 1000,
      maxDelay: 5000,
      backoffMultiplier: 1.5,
      retryCondition: (error) => {
        const errorCode = error?.code;
        
        // Firestore 可重試的錯誤
        const retryableCodes = [
          'unavailable',
          'deadline-exceeded',
          'resource-exhausted',
          'aborted',
          'internal',
          'unknown'
        ];
        
        return retryableCodes.includes(errorCode);
      },
      onRetry: (error, attempt) => {
        logger.data.warn(`Database operation failed, retrying... (attempt ${attempt})`, {
          error: error?.message,
          code: error?.code
        });
      },
      ...options
    });

    if (result.success) {
      return result.data!;
    }
    
    throw result.error;
  }

  /**
   * 睡眠函數
   */
  private static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 創建帶有重試機制的函數包裝器
   */
  static withRetry<T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    options: RetryOptions = {}
  ): (...args: T) => Promise<R> {
    return async (...args: T): Promise<R> => {
      const result = await this.retryWithBackoff(
        () => fn(...args),
        options
      );
      
      if (result.success) {
        return result.data!;
      }
      
      throw result.error;
    };
  }
}

export default RetryHelper;
